import React, { useState, useEffect, useMemo } from 'react'
import { useFullscreen } from '@/hooks/useFullscreen'
import { Maximize2, Minimize2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { Product } from '@shared/schema'

export default function MeatPriceAirport() {
  const { isFullscreen, enterFullscreen, exitFullscreen } = useFullscreen()
  const [currentTime, setCurrentTime] = useState(new Date())
  const [selectedCategories, setSelectedCategories] = useState<[string, string]>(['meat-beef', 'meat-chicken'])

  const { data: products = [], isLoading } = useQuery<Product[]>({
    queryKey: ['/api/products'],
  })

  // Filter only meat and seafood categories
  const meatCategories = ['meat-beef', 'meat-chicken', 'meat-pork', 'meat-hotpot', 'seafood']
  const meatProducts = useMemo(() => 
    products.filter(p => meatCategories.includes(p.category)),
    [products]
  )
  
  // Get unique categories
  const categories = useMemo(() => 
    Array.from(new Set(meatProducts.map(p => p.category))),
    [meatProducts]
  )
  
  // Filter products for each side
  const leftProducts = useMemo(() => 
    meatProducts.filter(p => p.category === selectedCategories[0]),
    [selectedCategories, meatProducts]
  )
  
  const rightProducts = useMemo(() => 
    meatProducts.filter(p => p.category === selectedCategories[1]),
    [selectedCategories, meatProducts]
  )

  // Generate random gates for products
  const randomGates = useMemo(() => {
    const gates: Record<number, string> = {}
    meatProducts.forEach(product => {
      const letter = String.fromCharCode(65 + Math.floor(Math.random() * 8)) // A-H
      const number = Math.floor(Math.random() * 65) + 3 // 3-67
      gates[product.id] = `${letter}${number}`
    })
    return gates
  }, [meatProducts])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).toUpperCase()
  }

  const getStatusText = (product: Product) => {
    return product.ourPrice < product.retailPrice ? 'SPECIAL' : 'REGULAR'
  }

  const getStatusColor = (product: Product) => {
    return product.ourPrice < product.retailPrice 
      ? 'text-red-500' 
      : 'text-green-500'
  }

  const getCategoryName = (category: string) => {
    switch(category) {
      case 'meat-beef': return 'BEEF'
      case 'meat-chicken': return 'CHICKEN'
      case 'meat-pork': return 'PORK'
      case 'meat-hotpot': return 'HOT POT'
      case 'seafood': return 'SEAFOOD'
      default: return category.toUpperCase()
    }
  }

  const splitFlapStyle = { 
    fontFamily: 'Solari, "Helvetica Rounded", "Arial Rounded MT Bold", "VAG Rounded", "DIN Alternate", "Roboto Mono", "JetBrains Mono", monospace',
    letterSpacing: '0.15em',
    fontWeight: 900,
    textTransform: 'uppercase' as const
  }

  const chineseStyle = {
    fontFamily: '"Noto Serif CJK SC", "Source Han Serif SC", "Songti SC", "STSong", "SimSun", "FangSong", "KaiTi", serif',
    letterSpacing: '0.05em',
    fontWeight: 700
  }

  const renderProductList = (products: Product[], side: 'left' | 'right') => (
    <div className="flex-1">
      <div className="grid grid-cols-[3fr_1.5fr_auto] gap-4 items-center border-b-2 border-amber-400 pb-1 mb-1 text-amber-400 font-bold">
        <div className="text-left text-3xl" style={splitFlapStyle}>DESCRIPTION</div>
        <div className="text-right pr-4 text-4xl" style={chineseStyle}>中文描述</div>
        <div className="text-left text-3xl" style={splitFlapStyle}>PRICE</div>
      </div>

      {products.map((product, index) => (
        <div
          key={product.id}
          className="grid grid-cols-[3fr_1.5fr_auto] gap-4 items-center py-1 border-b border-zinc-800 hover:bg-amber-400/5 transition-colors split-flap-row"
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div 
            className="text-left text-4xl font-extrabold tracking-tight whitespace-nowrap overflow-hidden text-ellipsis text-white split-flap-char" 
            style={splitFlapStyle}
          >
            {product.description.toUpperCase()}
          </div>
          
          <div 
            className="text-right text-5xl font-bold tracking-tight whitespace-nowrap overflow-hidden text-ellipsis text-amber-400 pr-4" 
            style={chineseStyle}
          >
            {product.chineseName || product.description}
          </div>
          
          <div 
            className="text-left text-5xl font-extrabold whitespace-nowrap split-flap-char"
            style={{...splitFlapStyle, color: '#ef4444'}}
          >
            ${product.ourPrice.toFixed(2)}
          </div>
        </div>
      ))}
    </div>
  )

  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-zinc-900 text-amber-400 flex items-center justify-center text-6xl" style={splitFlapStyle}>
        LOADING FLIGHT DATA...
      </div>
    )
  }

  return (
    <div className="h-screen w-screen bg-zinc-900 text-white overflow-hidden relative flex flex-col">
      <style jsx>{`
        .split-flap-char {
          text-shadow: 
            0 2px 4px rgba(0,0,0,0.8),
            0 -1px 0 rgba(255,255,255,0.1);
          background: linear-gradient(to bottom, 
            rgba(255,255,255,0.1) 0%, 
            transparent 50%, 
            rgba(0,0,0,0.3) 51%, 
            transparent 100%);
          padding: 0px 2px;
          border-radius: 2px;
        }
        
        .split-flap-row {
          background: linear-gradient(to bottom, #1a1a1a 0%, #0a0a0a 100%);
        }
      `}</style>

      {/* Header */}
      <div className="bg-gradient-to-b from-black to-zinc-900 border-b-2 border-amber-400 p-2 relative">
        <div className="flex items-center justify-between mb-1">
          <h1 
            className="font-extrabold tracking-wider text-6xl text-amber-400 drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]"
            style={splitFlapStyle}
          >
            MEAT PRICES TERMINAL
          </h1>
          
          <div className="flex items-center gap-4">
            <div className="flex gap-3">
              <select
                value={selectedCategories[0]}
                onChange={(e) => setSelectedCategories([e.target.value, selectedCategories[1]])}
                className="bg-black border-2 border-amber-400 text-amber-400 px-3 py-1 rounded font-bold text-base"
                style={splitFlapStyle}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{getCategoryName(cat)}</option>
                ))}
              </select>
              
              <select
                value={selectedCategories[1]}
                onChange={(e) => setSelectedCategories([selectedCategories[0], e.target.value])}
                className="bg-black border-2 border-amber-400 text-amber-400 px-3 py-1 rounded font-bold text-base"
                style={splitFlapStyle}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{getCategoryName(cat)}</option>
                ))}
              </select>
            </div>
            
            <div 
              className="text-2xl font-extrabold tracking-wider text-green-500 drop-shadow-[0_0_10px_rgba(34,197,94,0.5)]"
              style={splitFlapStyle}
            >
              {formatTime(currentTime)}
            </div>
            
            <button
              onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              className="text-amber-400 p-1 hover:bg-amber-400/10 rounded transition-colors"
            >
              {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </button>
          </div>
        </div>
        
        <div 
          className="text-lg tracking-wide text-amber-400"
          style={splitFlapStyle}
        >
          {formatDate(currentTime)}
        </div>
      </div>

      {/* Split Display */}
      <div className="flex-1 flex p-1 gap-2 overflow-hidden">
        <div className="flex-1 overflow-auto">
          <h2 
            className="text-4xl font-extrabold text-amber-400 mb-1 text-center border-b-2 border-amber-400 pb-1"
            style={splitFlapStyle}
          >
            {getCategoryName(selectedCategories[0])}
          </h2>
          {renderProductList(leftProducts, 'left')}
        </div>
        
        <div className="w-0.5 bg-amber-400"></div>
        
        <div className="flex-1 overflow-auto">
          <h2 
            className="text-4xl font-extrabold text-amber-400 mb-1 text-center border-b-2 border-amber-400 pb-1"
            style={splitFlapStyle}
          >
            {getCategoryName(selectedCategories[1])}
          </h2>
          {renderProductList(rightProducts, 'right')}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-t from-black to-zinc-900 border-t-2 border-amber-400 p-2 flex justify-center gap-6">
        <div className="text-amber-400 text-base" style={splitFlapStyle}>
          TERMINAL 1
        </div>
        <div className="text-green-500 flex items-center gap-2 text-base" style={splitFlapStyle}>
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          LIVE PRICES
        </div>
        <div className="text-amber-400 text-base" style={splitFlapStyle}>
          {leftProducts.length + rightProducts.length} ITEMS
        </div>
      </div>
    </div>
  )
}